import { useEffect, useState, useRef } from 'react'
import { Flex, Text } from '@fundamentals'
import { motion, useSpring, useTransform } from 'motion/react'
import { keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import { localizedStrings } from '@core/strings'
import { formatAreaSize } from '@modules/geolocation'

type DrawAreaSizeCardProps = {
  squareMeters: number
}

const fadeInSlideDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const AnimatedFlex = styled(Flex)`
  animation: ${fadeInSlideDown} 0.5s ease-out;
  opacity: 1;
`

const AnimatedNumber: React.FC<{
  value: number
  originalValue?: number
  unit?: 'KM' | 'M'
}> = ({ value, originalValue, unit = 'M' }) => {
  const spring = useSpring(value, { mass: 0.8, stiffness: 75, damping: 15 })
  const display = useTransform(spring, (current) => {
    if (unit === 'KM') {
      return current.toFixed(2).toLocaleString()
    } else {
      return Math.round(current).toLocaleString()
    }
  })
  const [color, setColor] = useState('black') // Default color
  const previousOriginalValueRef = useRef<number | undefined>(undefined)

  useEffect(() => {
    const previousValue = spring.get() // Get the current value before the update
    const isInitialLoad = previousValue === undefined || previousValue === value

    if (isInitialLoad) {
      // Reset to default color on initial load or no change
      setColor('black')
      previousOriginalValueRef.current = originalValue
      return
    }

    // Use originalValue for comparison if provided, otherwise use the display value
    const comparisonValue = originalValue ?? value
    const previousComparisonValue = originalValue
      ? previousOriginalValueRef.current
      : previousValue

    if (previousComparisonValue !== undefined) {
      const isIncreasing = comparisonValue > previousComparisonValue
      setColor(isIncreasing ? 'green' : 'red') // Set color based on direction
    }

    // Store the original value for next comparison
    previousOriginalValueRef.current = originalValue

    spring.set(value)

    // Listen for when the spring animation finishes
    spring.on('change', (current) => {
      const tolerance = unit === 'KM' ? 0.01 : 1
      if (Math.abs(current - value) < tolerance) {
        setColor('black') // Reset to default color when animation completes
      }
    })
  }, [spring, value, unit, originalValue])

  return <motion.span style={{ color, fontWeight: 500 }}>{display}</motion.span>
}

export const DrawAreaSizeCard: React.FC<DrawAreaSizeCardProps> = ({
  squareMeters,
}) => {
  const formattedArea = formatAreaSize(squareMeters)

  return (
    <AnimatedFlex
      alignItems={'center'}
      bgcolor={'white'}
      borderRadius={2}
      p={2}
      height={40}
    >
      <Text>
        {localizedStrings.totalDrawnAreaSize}
        <br />
        <AnimatedNumber
          value={formattedArea.value}
          originalValue={squareMeters}
          unit={formattedArea.unit}
        />
        {` ${formattedArea.unitName}`}
      </Text>
    </AnimatedFlex>
  )
}
